import React, { useMemo } from 'react';
import './WordCloudWidget.css';

/**
 * Nuage de mots — les termes des réponses libres, dimensionnés par leur fréquence.
 *
 * ─── PAS DE BIBLIOTHÈQUE ────────────────────────────────────────────────────────────────────
 * Les nuages tirent en général sur d3-cloud, qui place les mots par simulation physique dans un
 * canvas. Ici, un simple flux de `<span>` suffit : la taille de police porte l'information, le
 * navigateur fait le retour à la ligne, et le résultat reste sélectionnable, lisible par un
 * lecteur d'écran et réactif au redimensionnement — trois choses qu'un canvas perd.
 *
 * ─── L'ÉCHELLE EST LE SUJET ─────────────────────────────────────────────────────────────────
 * Dimensionner sur `count / max` écrase tout dès qu'un terme domine : avec un mot à 50 et le
 * reste à 1, tous les autres se retrouvent illisibles. L'échelle est donc RACINE CARRÉE, qui
 * comprime le haut sans aplatir le bas — le mot le plus fréquent reste le plus gros, mais les
 * autres restent lisibles.
 *
 * L'opacité suit la même échelle : la taille seule ne suffit pas à hiérarchiser quand deux mots
 * voisins ont des fréquences proches.
 */
const MIN_PX = 13;
const MAX_PX = 44;

const WordCloudWidget = ({ data, config }) => {
    const words = useMemo(() => {
        const rows = (Array.isArray(data) ? data : [])
            .filter((d) => d && d.word && Number(d.count) > 0);
        if (rows.length === 0) return [];

        const max = Math.max(...rows.map((d) => Number(d.count)));
        const min = Math.min(...rows.map((d) => Number(d.count)));
        const span = Math.max(1, max - min);

        return rows
            .slice(0, Number(config?.maxWords) || 60)
            .map((d) => {
                const ratio = Math.sqrt((Number(d.count) - min) / span);
                return {
                    ...d,
                    size: Math.round(MIN_PX + ratio * (MAX_PX - MIN_PX)),
                    opacity: 0.55 + ratio * 0.45,
                };
            });
    }, [data, config]);

    if (words.length === 0) return null;   // le cadre parent affiche l'état vide

    return (
        <div className="wordcloud-widget">
            {words.map((w) => (
                <span
                    key={w.word}
                    className="wordcloud-term"
                    style={{ fontSize: `${w.size}px`, opacity: w.opacity }}
                    /* Le compte exact au survol ET dans le titre accessible : la taille donne
                       l'ordre de grandeur, jamais la valeur. Un nuage sans ce recours ne se
                       lit pas, il se devine. */
                    title={`${w.word} — ${w.count}${w.weight != null ? ` (${w.weight} %)` : ''}`}
                >
                    {w.word}
                </span>
            ))}
        </div>
    );
};

export default WordCloudWidget;
